import { FC, useState } from 'react';
import { withStyles, Card, SupportedCurrencies, toBool } from 'vopay';
import styles from './ActionsMenu.module.scss';
import { UtilButton } from 'UI';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useHistory } from 'react-router-dom';
import { useClientData } from '@/providers/ClientData.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { TransferType } from '../AccountSummaryPage';

const cn = withStyles(styles);

type ActionsMenuProps = {
  isPremium: boolean;
  currency: SupportedCurrencies;
  onOpenFundTransferModal: (type: TransferType) => void;
};

const ActionsMenu: FC<ActionsMenuProps> = ({ isPremium, currency, onOpenFundTransferModal }) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });
  const history = useHistory();
  const { setOpenModule } = useOpenModule();
  const { onSubNavClick } = useNavMenu();
  const { response } = useClientData();
  const { permissions } = usePermissions();

  function isFundEnabled(currencyValue: string) {
    if (response.Country === 'CA') {
      return (
        (currencyValue === 'CAD' && toBool(response.EFTCollectEnabled)) ||
        (currencyValue === 'USD' && toBool(response.USDEFTCollectEnabled))
      );
    }

    if (response.Country === 'US') {
      return currencyValue === 'USD' && response.ACHFundEnabled === '1';
    }

    return false;
  }

  function isWithdrawEnabled(currencyValue: string) {
    if (response.Country === 'CA') {
      return (
        (currencyValue === 'CAD' && toBool(response.EFTSendEnabled)) ||
        (currencyValue === 'USD' && toBool(response.USDEFTSendEnabled))
      );
    }

    if (response.Country === 'US') {
      return currencyValue === 'USD' && response.ACHWithdrawEnabled === '1';
    }

    return false;
  }

  return (
    <DropdownMenu.Root onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <UtilButton iconRightProps={{ icon: 'pending', color: '#2f82ff' }} active={open} className={cn('utilButton')} />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={8}
          className={cn('contentContainer')}
          align="end"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <Card
            style={{
              padding: '4px',
              borderRadius: 10,
              borderColor: 'rgba(45, 45, 45, 0.05)',
              borderWidth: 1,
            }}
            className={cn('menu')}
          >
            {isPremium ? (
              <>
                <ActionItem
                  label={t('addFunds')}
                  onClick={() => {
                    onOpenFundTransferModal('Fund');
                  }}
                  disabled={!permissions.ManageInterestAccounts}
                />
                <ActionItem
                  label={t('withdrawBalanceButton')}
                  onClick={() => {
                    onOpenFundTransferModal('Withdraw');
                  }}
                  disabled={!permissions.ManageInterestAccounts}
                />
              </>
            ) : (
              <>
                <ActionItem
                  label={t('fund')}
                  onClick={() => {
                    setOpenModule('myAccount');
                    onSubNavClick('fundMyAccount', '/fund-my-account', 'Fund My Account');

                    history.push({
                      pathname: '/fund-my-account',
                      state: { currency },
                    });
                  }}
                  disabled={!isFundEnabled(currency)}
                />

                <ActionItem
                  label={t('withdraw')}
                  onClick={() => {
                    setOpenModule('myAccount');
                    onSubNavClick('withdrawMyAccount', '/withdraw-my-account', 'Withdraw My Account');

                    history.push({
                      pathname: '/withdraw-my-account',
                      state: { currency },
                    });
                  }}
                  disabled={!isWithdrawEnabled(currency)}
                />

                <ActionItem
                  label={t('collect')}
                  onClick={() => {
                    setOpenModule('collection');
                    onSubNavClick('singleCollection', '/single-collection', 'Single Collection');

                    history.push({
                      pathname: '/single-collection',
                      state: { currency },
                    });
                  }}
                  disabled={!isFundEnabled(currency)}
                />

                <ActionItem
                  label={t('pay')}
                  onClick={() => {
                    setOpenModule('payment');
                    onSubNavClick('singlePayment', '/single-payment', 'Single Payment');

                    history.push({
                      pathname: '/single-payment',
                      state: { currency },
                    });
                  }}
                  disabled={!isWithdrawEnabled(currency)}
                />
              </>
            )}
          </Card>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default ActionsMenu;

function ActionItem({ label, onClick, disabled }: { label: string; onClick: () => void; disabled: boolean }) {
  return (
    <DropdownMenu.Item asChild>
      <button disabled={disabled} onClick={onClick} className={cn('actionItem')}>
        {label}
      </button>
    </DropdownMenu.Item>
  );
}
